import { DefaultValue, atom, selector } from "recoil"

import {
  DEFAULT_DESIGN,
  type Design,
  type InstallArea,
} from "~/containers/Proposal/state/designs/design-model"
import type {
  PoisonedEstimate,
  Estimate,
  Savings,
  PoisonedSavings,
  SavingsQuery,
  EstimatesForDesignQuery,
} from "~/graphql/client"
import { selectDiff } from "~/utils/recoil"

import { getSystemSizeInWattsFromDesign } from "./solar-module"
import { proposalLocalState } from "../proposal/proposal-state"

export const selectedDesignIdState = selector<string>({
  key: `selectedDesignId`,
  get: ({ get }) => {
    const { designs } = get(proposalLocalState)

    return designs.find((d) => d.activatedAt)?.id ?? designs[0]?.id ?? ``
  },
})

export const designLocalState = atom<Design>({
  key: `designLocal`,
  default: DEFAULT_DESIGN,
})

export const designIdState = selector<string>({
  key: `designId`,
  get: ({ get }) => get(designLocalState).id,
})

export const designRemoteState = atom<Design>({
  key: `designRemote`,
  default: DEFAULT_DESIGN,
})

export const poisonedDesignState = selector<Design[`poison`]>({
  key: `poisonedDesign`,
  get: ({ get }) => {
    const design = get(designRemoteState)

    return design.poison
  },
})

export const estimatesRemoteState = atom<
  EstimatesForDesignQuery[`estimatesForDesign`]
>({
  key: `estimatesRemote`,
  default: [],
})

export const safeEstimatesRemoteState = selector<Estimate[]>({
  key: `safeEstimatesRemoteState`,
  get: ({ get }) => {
    const estimates = get(estimatesRemoteState)

    return estimates
      .filter((er) => er.__typename == `Estimate`)
      .map((er) => er as Estimate)
  },
})

export const poisonedEstimatesRemoteState = selector<PoisonedEstimate[]>({
  key: `poisonedEstimatesRemoteState`,
  get: ({ get }) => {
    const estimates = get(estimatesRemoteState)

    return estimates
      .filter((er) => er.__typename == `PoisonedEstimate`)
      .map((er) => er as PoisonedEstimate)
  },
})

export const designDiffState = selectDiff(designLocalState, designRemoteState)

export const installAreasForDesignState = selector<Record<string, InstallArea>>({
  key: `installAreasForProposal`,
  get: ({ get }) =>
    (get(designLocalState)?.installAreas ?? {}) as Record<string, InstallArea>,
  set: ({ set }, newValue) =>
    set(designLocalState, (current) => ({
      ...current,
      installAreas: newValue instanceof DefaultValue ? {} : newValue,
    })),
})

export const systemSizeInKwState = selector<number>({
  key: `systemSize`,
  get: ({ get }) => getSystemSizeInWattsFromDesign(get(designLocalState)) / 1000,
})

export const designLocalBatteryCompatibilityState = selector({
  key: `designLocalBatteryCompatibility`,
  get: ({ get }) => get(designLocalState).batteryCompatibility,
  set: ({ set }, value) => {
    if (value instanceof DefaultValue) return
    set(designLocalState, (design) => {
      return {
        ...design,
        batteryCompatibility: value,
      }
    })
  },
})

export const storageNameState = selector({
  key: `storageName`,
  get: ({ get }) => {
    const design = get(designLocalState)
    if (design) {
      const { energyBackup } = design
      if (energyBackup) {
        return `${energyBackup?.manufacturer?.name ?? ``} - ${
          energyBackup?.name ?? ``
        }`
      }
    }

    return `N/A`
  },
})

export const homeownerRebateValidState = selector<boolean>({
  key: `homeownerRebateValid`,
  get: ({ get }) => {
    const { homeownerRebateAmount } = get(designLocalState)

    return homeownerRebateAmount <= 3000 && homeownerRebateAmount >= 0
  },
})

export const savingsRemoteState = atom<
  SavingsQuery[`savingsForDesignId`] | null
>({
  key: `savingsRemoteState`,
  default: null,
})

export const safeSavingsState = selector<Savings | null>({
  key: `safeSavingsState`,
  get: ({ get }) => {
    const savings = get(savingsRemoteState)

    return savings?.__typename === `Savings` ? (savings as Savings) : null
  },
})

export const poisonedSavingsState = selector<PoisonedSavings | null>({
  key: `poisonedSavingsState`,
  get: ({ get }) => {
    const savings = get(savingsRemoteState)

    return savings?.__typename === `PoisonedSavings`
      ? (savings as PoisonedSavings)
      : null
  },
})

export type ContractReadinessValidation = {
  isReady: boolean
  missingSystemSize: boolean
  missingPrimaryInverter: boolean
}

export const contractReadinessState = selector<ContractReadinessValidation>({
  key: `contractReadiness`,
  get: ({ get }) => {
    const systemSizeInKw = get(systemSizeInKwState)
    const design = get(designLocalState)

    const missingSystemSize = systemSizeInKw === 0
    const missingPrimaryInverter = !design.inverter && !design.inverterId

    return {
      isReady: !missingSystemSize && !missingPrimaryInverter,
      missingSystemSize,
      missingPrimaryInverter,
    }
  },
})
